// Pieces the tensor screens share: the open workbook and its rows, the
// month switch, money, the 지출/수입 switch and item choosers.
import { type KeyboardEvent, type Ref, useId, useMemo } from 'react'

import { Icon, IconButton } from '../../ui/common'
import type { Id, RowKind, TensorItem, TensorRole, TensorWorkbook } from '../api'
import { addMonths, amountInput, monthTitle, won } from '../format'
import { type Book, type ShownRow, shownRows } from '../model'
import { useT } from '../store'
import { categoryPath, itemsOf } from '../views'

export interface Current {
  workbook: TensorWorkbook
  book: Book | undefined
  /** rows with this device's waiting changes, not deleted */
  rows: ShownRow[]
  meId: Id
}

const selectWorkbook = (s: { current: Id; workbooks: Record<number, TensorWorkbook> }) => s.workbooks[s.current]

/** The workbook on screen (undefined when there is none yet). */
export function useCurrent(): Current | undefined {
  const workbook = useT(selectWorkbook)
  const book = useT((s) => (workbook ? s.books[workbook.id] : undefined))
  const ops = useT((s) => s.ops)
  const meId = useT((s) => s.me?.id ?? 0)
  const wid = workbook?.id ?? 0
  const rows = useMemo(() => (wid ? shownRows(book, ops, wid, meId) : []), [wid, book, ops, meId])
  if (!workbook) return undefined
  return { workbook, book, rows, meId }
}

export function MonthSwitch({ month, onChange }: { month: string; onChange: (m: string) => void }) {
  return (
    <div className="t-month" role="group" aria-label="달">
      <IconButton label="이전 달" onClick={() => onChange(addMonths(month, -1))}>
        <Icon name="prev" />
      </IconButton>
      <h2 aria-live="polite">{monthTitle(month)}</h2>
      <IconButton label="다음 달" onClick={() => onChange(addMonths(month, 1))}>
        <Icon name="next" />
      </IconButton>
    </div>
  )
}

/** An amount: income with a +, expense as it is. */
export function Money({ amount, kind }: { amount: number; kind?: RowKind }) {
  return (
    <span className={`t-money${kind === 'income' ? ' income' : ''}`}>
      {kind === 'income' ? '+' : ''}
      {won(amount)}
    </span>
  )
}

export function KindSwitch({ kind, onChange }: { kind: RowKind; onChange: (k: RowKind) => void }) {
  return (
    <div className="t-seg" role="group" aria-label="구분">
      <button type="button" aria-pressed={kind === 'expense'} onClick={() => onChange('expense')}>
        지출
      </button>
      <button type="button" aria-pressed={kind === 'income'} className="income" onClick={() => onChange('income')}>
        수입
      </button>
    </div>
  )
}

/** A select of a dimension's items ("(없음)" first). A deleted item that is
    chosen stays in the list, marked. */
export function ItemSelect(props: {
  items: Record<number, TensorItem>
  role: TensorRole
  value: Id | null
  onChange: (id: Id | null) => void
  id?: string
}) {
  const { items, role, value } = props
  const options: { id: Id; label: string }[] = []
  for (const top of itemsOf(items, role)) {
    options.push({ id: top.id, label: top.name })
    if (role === 'category')
      for (const c of itemsOf(items, role, top.id)) options.push({ id: c.id, label: `${top.name} > ${c.name}` })
  }
  if (value && !options.some((o) => o.id === value) && items[value]) {
    const label = role === 'category' ? categoryPath(items, value) : items[value].name
    options.push({ id: value, label: `${label} (지운 항목)` })
  }
  return (
    <select id={props.id} value={value ?? 0} onChange={(e) => props.onChange(Number(e.target.value) || null)}>
      <option value={0}>(없음)</option>
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {o.label}
        </option>
      ))}
    </select>
  )
}

/** "비공개" or "공개" */
export function VisibilityBadge({ workbook }: { workbook: TensorWorkbook }) {
  return workbook.visibility === 'family' ? (
    <span className="tag t-family">공개</span>
  ) : (
    <span className="tag t-private">비공개</span>
  )
}

/** A bar of part out of whole (over the whole: full and marked). */
export function Bar({ part, whole, label }: { part: number; whole: number; label?: string }) {
  const over = whole > 0 && part > whole
  const pct = whole > 0 ? Math.min(100, (part / whole) * 100) : part > 0 ? 100 : 0
  return (
    <span className={`t-track${over ? ' over' : ''}${whole <= 0 ? ' none' : ''}`} aria-label={label}>
      <span className="t-fill" style={{ width: `${pct}%` }} />
    </span>
  )
}

/** The amount input: number keys on a phone, digits grouped, "원" after it.
    enterKeyHint: the phone keyboard's key (완료, or 다음 when Enter goes on). */
export function AmountField(props: {
  value: string
  onChange: (v: string) => void
  label?: string
  inputRef?: Ref<HTMLInputElement>
  enterKeyHint?: 'done' | 'next'
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void
}) {
  const id = useId()
  return (
    <div className="field">
      <label className="field-label" htmlFor={id}>
        {props.label ?? '금액'}
      </label>
      <span className="t-amount">
        <input
          id={id}
          ref={props.inputRef}
          inputMode="numeric"
          autoComplete="off"
          enterKeyHint={props.enterKeyHint ?? 'done'}
          placeholder="0"
          value={props.value}
          onChange={(e) => props.onChange(amountInput(e.target.value))}
          onKeyDown={props.onKeyDown}
        />
        <span aria-hidden>원</span>
      </span>
    </div>
  )
}
